/**
 * Credencial persistente del marcador (T018; research R-3; implementation-notes 003 §4).
 *
 * Un solo store IndexedDB `fermin-marker` (`idb-keyval`, base `fermin-marker`, store
 * `credential`) con cinco claves:
 *
 * | clave           | valor                                                   |
 * |-----------------|---------------------------------------------------------|
 * | `session_token` | token opaco de sesión (`POST /activate`), `string`      |
 * | `terminal_key`  | `CryptoKey` HMAC-SHA256 **no exportable** (`['sign']`)  |
 * | `key_version`   | versión de `terminal_key` acusada al servidor, `number` |
 * | `clock`         | `{ offset_ms, samples[], last_sync_at }` (R-9)          |
 * | `me`            | último `TerminalMe` (pinta sede y terminal sin red)     |
 *
 * Nada en `localStorage`/`sessionStorage`: la `CryptoKey` obliga a IndexedDB y no tiene sentido
 * repartir la credencial en dos sitios. Si el navegador borra el almacenamiento, el gate de `/`
 * vuelve al formulario de código.
 *
 * El acceso pasa por la interfaz `CredentialStore` para que las pruebas (jsdom no implementa
 * `indexedDB`) inyecten un store en memoria con `setCredentialStore(createMemoryCredentialStore())`.
 * Nunca se loguea el token ni la clave.
 */
import { createStore, del, get, set, type UseStore } from 'idb-keyval';

import { TerminalMeSchema, type TerminalMe } from '@fermin/contracts';

export const CREDENTIAL_DB_NAME = 'fermin-marker';
export const CREDENTIAL_STORE_NAME = 'credential';

export const CREDENTIAL_KEYS = {
  sessionToken: 'session_token',
  terminalKey: 'terminal_key',
  keyVersion: 'key_version',
  clock: 'clock',
  me: 'me',
} as const;
export type CredentialKey = (typeof CREDENTIAL_KEYS)[keyof typeof CREDENTIAL_KEYS];

/** Sincronización de reloj persistida (research R-9): offset vigente, muestras y último sync. */
export interface ClockSnapshot {
  /** Mediana de `samples` en ms (`server − local`). */
  offset_ms: number;
  /** Últimas muestras de offset (máximo 5), la más reciente al final. */
  samples: number[];
  /** Epoch del servidor (ms) de la última muestra; `null` si nunca hubo sincronización. */
  last_sync_at: number | null;
}

export interface CredentialStore {
  get(key: CredentialKey): Promise<unknown>;
  set(key: CredentialKey, value: unknown): Promise<void>;
  del(key: CredentialKey): Promise<void>;
}

function isCryptoKey(value: unknown): value is CryptoKey {
  if (typeof CryptoKey !== 'undefined' && value instanceof CryptoKey) {
    return true;
  }
  // Realm distinto (p. ej. jsdom + WebCrypto de Node): se acepta por forma.
  return (
    typeof value === 'object' &&
    value !== null &&
    'type' in value &&
    'algorithm' in value &&
    'usages' in value
  );
}

/** `true` si el valor leído es una clave HMAC secreta apta para firmar. */
export function isTerminalKey(value: unknown): value is CryptoKey {
  return isCryptoKey(value) && value.type === 'secret' && value.usages.includes('sign');
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** `true` si el valor leído tiene la forma de `ClockSnapshot`. */
export function isClockSnapshot(value: unknown): value is ClockSnapshot {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    isFiniteNumber(record.offset_ms) &&
    Array.isArray(record.samples) &&
    record.samples.every(isFiniteNumber) &&
    (record.last_sync_at === null || isFiniteNumber(record.last_sync_at))
  );
}

/** Store real sobre IndexedDB. La base se abre en el primer acceso, no al importar el módulo. */
export function createIdbCredentialStore(): CredentialStore {
  let store: UseStore | undefined;
  const openStore = (): UseStore => {
    store ??= createStore(CREDENTIAL_DB_NAME, CREDENTIAL_STORE_NAME);
    return store;
  };
  return {
    get: (key) => get(key, openStore()),
    set: (key, value) => set(key, value, openStore()),
    del: (key) => del(key, openStore()),
  };
}

/** Store en memoria: pruebas unitarias y entornos sin IndexedDB. Se pierde al recargar. */
export function createMemoryCredentialStore(): CredentialStore {
  const values = new Map<CredentialKey, unknown>();
  return {
    get: (key) => Promise.resolve(values.get(key)),
    set(key, value) {
      values.set(key, value);
      return Promise.resolve();
    },
    del(key) {
      values.delete(key);
      return Promise.resolve();
    },
  };
}

let activeStore: CredentialStore | undefined;

/** Store activo; por defecto IndexedDB. */
export function getCredentialStore(): CredentialStore {
  activeStore ??= createIdbCredentialStore();
  return activeStore;
}

/** Sustituye el store (pruebas). `null` vuelve al store por defecto. */
export function setCredentialStore(store: CredentialStore | null): void {
  activeStore = store ?? undefined;
}

// ---------------------------------------------------------------------------
// Token de sesión
// ---------------------------------------------------------------------------

/** Token opaco de sesión o `null` si el marcador no está configurado. */
export async function getSessionToken(): Promise<string | null> {
  const value = await getCredentialStore().get(CREDENTIAL_KEYS.sessionToken);
  return typeof value === 'string' && value.length > 0 ? value : null;
}

export function setSessionToken(token: string): Promise<void> {
  return getCredentialStore().set(CREDENTIAL_KEYS.sessionToken, token);
}

/**
 * Borra la credencial completa (token, clave de terminal, versión y snapshot de `me`) tras un
 * `401`, un `revoked` o un cierre `4401`. La sincronización de reloj (`clock`) se conserva: no
 * depende de la sesión y evita arrancar la siguiente activación sin hora.
 */
export async function clearCredential(): Promise<void> {
  const store = getCredentialStore();
  await Promise.all([
    store.del(CREDENTIAL_KEYS.sessionToken),
    store.del(CREDENTIAL_KEYS.terminalKey),
    store.del(CREDENTIAL_KEYS.keyVersion),
    store.del(CREDENTIAL_KEYS.me),
  ]);
}

// ---------------------------------------------------------------------------
// Clave de terminal (research R-10)
// ---------------------------------------------------------------------------

/** `CryptoKey` HMAC no exportable o `null`. Un valor corrupto se trata como ausencia de clave. */
export async function getTerminalKey(): Promise<CryptoKey | null> {
  const value = await getCredentialStore().get(CREDENTIAL_KEYS.terminalKey);
  return isTerminalKey(value) ? value : null;
}

export function setTerminalKey(key: CryptoKey): Promise<void> {
  return getCredentialStore().set(CREDENTIAL_KEYS.terminalKey, key);
}

/** Versión de la clave acusada al servidor (`terminal_key_ack`) o `null`. */
export async function getKeyVersion(): Promise<number | null> {
  const value = await getCredentialStore().get(CREDENTIAL_KEYS.keyVersion);
  return typeof value === 'number' && Number.isInteger(value) && value >= 1 ? value : null;
}

export function setKeyVersion(version: number): Promise<void> {
  return getCredentialStore().set(CREDENTIAL_KEYS.keyVersion, version);
}

// ---------------------------------------------------------------------------
// Reloj (research R-9)
// ---------------------------------------------------------------------------

export async function getClock(): Promise<ClockSnapshot | null> {
  const value = await getCredentialStore().get(CREDENTIAL_KEYS.clock);
  return isClockSnapshot(value) ? value : null;
}

export function setClock(snapshot: ClockSnapshot): Promise<void> {
  return getCredentialStore().set(CREDENTIAL_KEYS.clock, snapshot);
}

// ---------------------------------------------------------------------------
// Snapshot de `TerminalMe`
// ---------------------------------------------------------------------------

/** Último `TerminalMe` recibido, validado con el contrato; `null` si falta o no es válido. */
export async function getMeSnapshot(): Promise<TerminalMe | null> {
  const value = await getCredentialStore().get(CREDENTIAL_KEYS.me);
  const parsed = TerminalMeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function setMeSnapshot(me: TerminalMe): Promise<void> {
  return getCredentialStore().set(CREDENTIAL_KEYS.me, me);
}
